import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../services/adminApi";
import StatCard from "../components/StatCard";
import usePolling from "../hooks/usePolling";
import Pagination from "../components/Pagination";

export default function Bonuses() {
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [paging, setPaging] = useState({ page: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({ source: "", userId: "" });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [summary, eventsRes] = await Promise.all([
        adminApi.getMlmBonusOverview(),
        adminApi.getMlmBonusEvents({
          page: paging.page,
          pageSize: paging.pageSize,
          ...filters,
        }),
      ]);
      setOverview(summary);
      setEvents(eventsRes.items || []);
      setPaging((prev) => ({ ...prev, total: eventsRes.total || 0 }));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load bonus data");
    }
  }, [filters, paging.page, paging.pageSize]);

  usePolling(load, 30000, [filters.source, filters.userId, paging.page, paging.pageSize]);

  useEffect(() => {
    setPaging((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [filters.source, filters.userId]);

  return (
    <div>
      <h2>Aiarbix and Bonus Monitoring</h2>
      <div className="row" style={{ marginBottom: 10 }}>
        <select
          value={filters.source}
          onChange={(e) => setFilters((prev) => ({ ...prev, source: e.target.value }))}
        >
          <option value="">All Sources</option>
          <option value="commission">Commission</option>
          <option value="staking_revenue">Staking Revenue</option>
          <option value="rapid_bonus">Rapid Bonus</option>
          <option value="introduction_bonus">Introduction Bonus</option>
          <option value="loyalty_bonus">Loyalty Bonus</option>
          <option value="super_bonus">Super Bonus</option>
        </select>
        <input
          value={filters.userId}
          onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
          placeholder="Filter by user ID"
        />
        <button className="btn-primary" onClick={load}>
          Refresh
        </button>
      </div>
      {error ? <div className="error">{error}</div> : null}

      {overview ? (
        <div className="stat-grid" style={{ marginBottom: 14 }}>
          <StatCard
            label="Commissions"
            value={`${overview.commissions.count} / ${overview.commissions.totalAmount.toFixed(2)}`}
          />
          <StatCard
            label="Staking Revenue"
            value={`${overview.stakingRevenue.count} / ${overview.stakingRevenue.totalAmount.toFixed(2)}`}
          />
          <StatCard
            label="Rapid Bonus"
            value={`${overview.rapidBonus.count} / ${overview.rapidBonus.totalAmount.toFixed(2)}`}
          />
          <StatCard
            label="Introduction Bonus"
            value={`${overview.introductionBonus.count} / ${overview.introductionBonus.totalAmount.toFixed(2)}`}
          />
          <StatCard
            label="Loyalty Bonus"
            value={`${overview.loyaltyBonus.count} / ${overview.loyaltyBonus.totalAmount.toFixed(2)}`}
          />
          <StatCard
            label="Super Bonus"
            value={`${overview.superBonus.count} / ${overview.superBonus.totalAmount.toFixed(2)}`}
          />
        </div>
      ) : null}

      <div className="card">
        <h3>Bonus Events Feed</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Source</th>
                <th>User</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {events.map((item) => (
                <tr key={`${item.source}-${item.id}`}>
                  <td>{new Date(item.createdAt).toLocaleString()}</td>
                  <td>{item.source}</td>
                  <td>{item.user?.email || item.userId || "-"}</td>
                  <td>{item.amount || 0}</td>
                  <td>{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={paging.page}
          pageSize={paging.pageSize}
          total={paging.total}
          onPageChange={(nextPage) =>
            setPaging((prev) => ({
              ...prev,
              page: nextPage,
            }))
          }
        />
      </div>
    </div>
  );
}
